// Considered: LaneMarkerAtom — rejected because the filter lane marker is a single-word tag; a
// row header here carries a glyph, a name, an optional note, a pending caveat and an intent count.
// See spec Reuse Audit (arc-editor-canvas-v1.md).
import React from 'react';
import { useTranslation } from 'localization';
import GlyphAtom from '../../atoms/Glyph/GlyphAtom';
import TagAtom from '../../atoms/Tag/TagAtom';
import './ArcLaneLabelMolecule.scss';

export type ArcLaneLabelTone = 'system' | 'any';

export interface ArcLaneLabelMoleculeProps {
  /** Lucide icon name, without the `lucide:` prefix (`arc-lane.jsx:248`). */
  glyph: string;
  name: string;
  /** Short italicized caption under the name (a perspective's own note). */
  note?: string;
  /** Intents in this lane, across all stages. Omitted for the system lane (no intent count). */
  count?: number;
  /** True when the perspective is not declared on the base schema — a claim, not a fact. */
  pending?: boolean;
  /** `system` for the system lane, `any` for the synthesized Unattributed lane. */
  tone?: ArcLaneLabelTone;
  className?: string;
}

/** The row header for one lane in the stage x lane grid: glyph, name, optional note, a pending
 * caveat when the perspective isn't on every type, and the lane's total intent count
 * (`arc-lane.jsx:246-263`). */
const ArcLaneLabelMolecule: React.FC<ArcLaneLabelMoleculeProps> = ({
  glyph,
  name,
  note,
  count,
  pending = false,
  tone,
  className = '',
}) => {
  const { t } = useTranslation();

  return (
    <div
      className={`arc-lane-label ${className}`.trim()}
      data-tone={tone}
      data-pending={pending ? 'true' : undefined}
    >
      <GlyphAtom glyph={`lucide:${glyph}`} className="arc-lane-label__glyph" aria-hidden />
      <span className="arc-lane-label__main">
        <b className="arc-lane-label__name" data-datum="canvas.name#2" data-kind="strong">
          {name}
        </b>
        {note && <i className="arc-lane-label__note">{note}</i>}
        {pending && (
          <TagAtom
            wrap
            size="small"
            shape="pill"
            tone="warn"
            icon="lucide:help-circle"
            label={t('studio.arcEditor.laneLabel.pendingCaveat')}
            title={t('studio.arcEditor.laneLabel.pendingTitle')}
            className="arc-lane-label__pending"
            data-datum="canvas.not-on-every-type-checked-at-attach"
            data-kind="status-mark"
          />
        )}
        {count != null && (
          <span className="arc-lane-label__count" data-datum="canvas.intent" data-kind="count">
            {t('studio.arcEditor.laneLabel.intentCount', { count })}
          </span>
        )}
      </span>
    </div>
  );
};

ArcLaneLabelMolecule.displayName = 'ArcLaneLabelMolecule';

export default ArcLaneLabelMolecule;
